/**
 * GuestQueue Help Center progress navigation.
 *
 * Tracks the current documentation section, highlights the active step,
 * marks previous steps as completed, and updates the progress line.
 */
const initializeHelpProgress = () => {
    const navigation = document.querySelector(".help-progress-nav");

    if (!navigation) {
        return;
    }

    const progressFill = navigation.querySelector(".help-progress-fill");

    const links = Array.from(
        navigation.querySelectorAll(
            ".help-progress-link[data-section]"
        )
    );

    if (!progressFill || !links.length) {
        return;
    }

    const items = links
        .map((link, index) => {
            const sectionId = link.dataset.section;
            const section = document.getElementById(sectionId);

            if (!section) {
                return null;
            }

            return {
                index,
                link,
                section,
            };
        })
        .filter(Boolean);

    if (!items.length) {
        return;
    }

    const reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    );

    const desktopQuery = window.matchMedia(
        "(min-width: 992px)"
    );

    let activeIndex = 0;
    let animationFrame = null;
    let isProgrammaticScroll = false;
    let programmaticScrollTimeout = null;

    /**
     * Find the height of a fixed or sticky page header.
     */
    const getHeaderOffset = () => {
        const header = document.querySelector(
            [
                ".navbar.fixed-top",
                ".navbar.sticky-top",
                "header.fixed-top",
                "header.sticky-top",
            ].join(",")
        );

        if (!header) {
            return 0;
        }

        return header.getBoundingClientRect().height;
    };

    /**
     * Update the visual progress line.
     */
    const updateProgressFill = (index) => {
        const percentage =
            items.length > 1
                ? (index / (items.length - 1)) * 100
                : 100;

        if (desktopQuery.matches) {
            progressFill.style.width = "100%";
            progressFill.style.height = `${percentage}%`;
        } else {
            progressFill.style.height = "100%";
            progressFill.style.width = `${percentage}%`;
        }
    };

    /**
     * Apply active and completed states.
     */
    const updateStepStates = (index) => {
        const safeIndex = Math.max(
            0,
            Math.min(index, items.length - 1)
        );

        activeIndex = safeIndex;

        items.forEach((item, itemIndex) => {
            const isActive = itemIndex === safeIndex;
            const isCompleted = itemIndex < safeIndex;

            item.link.classList.toggle("active", isActive);
            item.link.classList.toggle(
                "completed",
                isCompleted
            );

            item.section.classList.toggle(
                "is-current",
                isActive
            );

            item.section.classList.toggle(
                "is-completed",
                isCompleted
            );

            if (isActive) {
                item.link.setAttribute(
                    "aria-current",
                    "step"
                );
            } else {
                item.link.removeAttribute("aria-current");
            }
        });

        updateProgressFill(safeIndex);
    };

    /**
     * Determine which section is currently passing the viewport trigger.
     */
    const findCurrentSection = () => {
        if (isProgrammaticScroll) {
            return;
        }

        const headerOffset = getHeaderOffset();

        const triggerPosition =
            headerOffset + window.innerHeight * 0.3;

        let currentIndex = 0;

        items.forEach((item, index) => {
            const sectionRect =
                item.section.getBoundingClientRect();

            if (sectionRect.top <= triggerPosition) {
                currentIndex = index;
            }
        });

        const viewportBottom =
            window.scrollY + window.innerHeight;

        const documentBottom =
            document.documentElement.scrollHeight;

        if (viewportBottom >= documentBottom - 4) {
            currentIndex = items.length - 1;
        }

        if (currentIndex !== activeIndex) {
            updateStepStates(currentIndex);
        }
    };

    /**
     * Throttle scroll handling with requestAnimationFrame.
     */
    const requestProgressUpdate = () => {
        if (animationFrame !== null) {
            return;
        }

        animationFrame = window.requestAnimationFrame(
            () => {
                findCurrentSection();
                animationFrame = null;
            }
        );
    };

    /**
     * Scroll to one of the help sections.
     */
    const scrollToSection = (
        section,
        behavior = "smooth"
    ) => {
        const headerOffset = getHeaderOffset();

        const sectionTop =
            section.getBoundingClientRect().top +
            window.scrollY -
            headerOffset -
            24;

        window.scrollTo({
            top: Math.max(sectionTop, 0),
            behavior:
                reducedMotionQuery.matches
                    ? "auto"
                    : behavior,
        });
    };

    /**
     * Temporarily pause automatic scroll tracking while a navigation link
     * performs smooth scrolling.
     */
    const beginProgrammaticScroll = () => {
        isProgrammaticScroll = true;

        window.clearTimeout(
            programmaticScrollTimeout
        );

        programmaticScrollTimeout =
            window.setTimeout(() => {
                isProgrammaticScroll = false;
                requestProgressUpdate();
            }, 800);
    };

    links.forEach((link) => {
        link.addEventListener("click", (event) => {
            const sectionId = link.dataset.section;

            const itemIndex = items.findIndex(
                (item) =>
                    item.section.id === sectionId
            );

            if (itemIndex < 0) {
                return;
            }

            event.preventDefault();

            beginProgrammaticScroll();
            updateStepStates(itemIndex);

            scrollToSection(
                items[itemIndex].section
            );

            history.replaceState(
                null,
                "",
                `#${sectionId}`
            );
        });
    });

    window.addEventListener(
        "scroll",
        requestProgressUpdate,
        {
            passive: true,
        }
    );

    window.addEventListener("resize", () => {
        updateProgressFill(activeIndex);
        requestProgressUpdate();
    });

    window.addEventListener(
        "load",
        requestProgressUpdate
    );

    desktopQuery.addEventListener?.(
        "change",
        () => {
            updateProgressFill(activeIndex);
        }
    );

    /**
     * Open the section from the current URL hash.
     */
    const initialSectionId =
        window.location.hash.replace("#", "");

    const initialIndex = items.findIndex(
        (item) =>
            item.section.id === initialSectionId
    );

    if (initialIndex >= 0) {
        updateStepStates(initialIndex);

        window.requestAnimationFrame(() => {
            scrollToSection(
                items[initialIndex].section,
                "auto"
            );
        });
    } else {
        findCurrentSection();
    }
};

if (document.readyState === "loading") {
    document.addEventListener(
        "DOMContentLoaded",
        initializeHelpProgress,
        {
            once: true,
        }
    );
} else {
    initializeHelpProgress();
}